/**
 * @fileoverview Serviço de contexto global para sincronização otimizada de dados
 * @description Gerencia timestamps de sincronização e carregamento inteligente de dados
 * @module GlobalContextService
 * @since 1.0.0
 */

import { apiRequestJson } from '../api/apiService.js';

/**
 * Chaves do localStorage para armazenar dados
 */
const STORAGE_KEYS = {
  GLOBAL_CONTEXT: 'vikanaweb_global_context',
  PRODUCTS: 'vikanaweb_products',
  CATEGORIES: 'vikanaweb_categories',
  BRANDS: 'vikanaweb_brands',
  PROMOTIONS: 'vikanaweb_promotions'
};

/**
 * Serviço de contexto global
 * @class GlobalContextService
 */
class GlobalContextService {
  /**
   * Busca o contexto global do servidor
   * @async
   * @returns {Promise<Object>} Contexto global com timestamps
   */
  async fetchGlobalContext() {
    try {
      const response = await apiRequestJson('global_context');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar contexto global:', error);
      throw error;
    }
  }

  /**
   * Obtém o contexto global armazenado localmente
   * @returns {Object|null} Contexto local ou null se não existir
   */
  getLocalContext() {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.GLOBAL_CONTEXT);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Erro ao ler contexto local:', error);
      return null;
    }
  }

  /**
   * Armazena o contexto global no localStorage
   * @param {Object} context - Contexto global a ser armazenado
   */
  setLocalContext(context) {
    try {
      localStorage.setItem(STORAGE_KEYS.GLOBAL_CONTEXT, JSON.stringify(context));
    } catch (error) {
      console.error('Erro ao salvar contexto local:', error);
    }
  }

  /**
   * Busca e armazena produtos
   * @async
   * @returns {Promise<boolean>} Sucesso na operação
   */
  async fetchAndStoreProducts() {
    try {
      const response = await apiRequestJson('products_list');
      if (response.success && response.data) {
        localStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(response.data));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
      return false;
    }
  }

  /**
   * Busca e armazena categorias
   * @async
   * @returns {Promise<boolean>} Sucesso na operação
   */
  async fetchAndStoreCategories() {
    try {
      const response = await apiRequestJson('products_categories');
      if (response.success && response.data) {
        localStorage.setItem(STORAGE_KEYS.CATEGORIES, JSON.stringify(response.data));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao buscar categorias:', error);
      return false;
    }
  }

  /**
   * Busca e armazena marcas
   * @async
   * @returns {Promise<boolean>} Sucesso na operação
   */
  async fetchAndStoreBrands() {
    try {
      const response = await apiRequestJson('products_brands');
      if (response.success && response.data) {
        localStorage.setItem(STORAGE_KEYS.BRANDS, JSON.stringify(response.data));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao buscar marcas:', error);
      return false;
    }
  }

  /**
   * Busca e armazena promoções
   * @async
   * @returns {Promise<boolean>} Sucesso na operação
   */
  async fetchAndStorePromotions() {
    try {
      const response = await apiRequestJson('products_promotions_list');
      if (response.success && response.data) {
        localStorage.setItem(STORAGE_KEYS.PROMOTIONS, JSON.stringify(response.data));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao buscar promoções:', error);
      return false;
    }
  }

  /**
   * Compara timestamps e retorna quais dados precisam ser atualizados
   * @param {Object} remoteContext - Contexto remoto
   * @param {Object} localContext - Contexto local
   * @returns {Array<string>} Lista de dados que precisam ser atualizados
   */
  getUpdatesNeeded(remoteContext, localContext) {
    const updates = [];
    
    const compareTimestamp = (remote, local) => {
      if (!local) return true;
      return new Date(remote) > new Date(local);
    };

    if (compareTimestamp(remoteContext.products, localContext?.products)) {
      updates.push('products');
    }
    
    if (compareTimestamp(remoteContext.categories, localContext?.categories)) {
      updates.push('categories');
    }
    
    if (compareTimestamp(remoteContext.brands, localContext?.brands)) {
      updates.push('brands');
    }
    
    if (compareTimestamp(remoteContext.promotions, localContext?.promotions)) {
      updates.push('promotions');
    }

    return updates;
  }

  /**
   * Executa sincronização automática a cada 5 minutos
   * @async
   * @returns {Promise<Object>} Resultado da sincronização
   */
  async autoSync() {
    try {
      const localContext = this.getLocalContext();
      if (!localContext) {
        return await this.syncData();
      }

      const remoteContext = await this.fetchGlobalContext();
      const updatesNeeded = this.getUpdatesNeeded(remoteContext, localContext);
      
      let result = {
        success: true,
        updated: [],
        errors: [],
        isAutoSync: true
      };

      if (updatesNeeded.length === 0) {
        console.log('✨ Auto-sync: Todos os dados estão atualizados');
        // Inclui todos os tipos para garantir hidratação do Redux
        result.updated = ['products', 'categories', 'brands', 'promotions'];
        return result;
      }

      console.log('🔄 Auto-sync: Atualizações necessárias:', updatesNeeded);

      const updateOperations = {
        products: () => this.fetchAndStoreProducts(),
        categories: () => this.fetchAndStoreCategories(),
        brands: () => this.fetchAndStoreBrands(),
        promotions: () => this.fetchAndStorePromotions()
      };

      console.log(updateOperations);

      for (const updateType of updatesNeeded) {
        try {
          const success = await updateOperations[updateType]();
          if (success) {
            result.updated.push(updateType);
            console.log(`✅ Auto-sync: ${updateType} atualizado`);
          } else {
            result.errors.push(`Falha no auto-sync de ${updateType}`);
          }
        } catch (error) {
          result.errors.push(`Erro no auto-sync de ${updateType}: ${error.message}`);
        }
      }

      this.setLocalContext(remoteContext);
      console.log('🎉 Auto-sync concluído:', result);
      return result;
      
    } catch (error) {
      console.error('❌ Erro no auto-sync:', error);
      return {
        success: false,
        error: error.message,
        updated: [],
        errors: [error.message],
        isAutoSync: true
      };
    }
  }

  /**
   * Executa sincronização completa dos dados
   * @async
   * @returns {Promise<Object>} Resultado da sincronização
   */
  async syncData() {
    try {
      console.log('🔄 Iniciando sincronização de dados...');
      
      const remoteContext = await this.fetchGlobalContext();
      const localContext = this.getLocalContext();
      
      console.log('📊 Contexto remoto:', remoteContext);
      console.log('💾 Contexto local:', localContext);

      let result = {
        success: true,
        updated: [],
        errors: [],
        isFirstLoad: !localContext
      };

      if (!localContext) {
        console.log('🆕 Primeiro carregamento - baixando todos os dados');
        
        const operations = [
          { name: 'products', fn: () => this.fetchAndStoreProducts() },
          { name: 'categories', fn: () => this.fetchAndStoreCategories() },
          { name: 'brands', fn: () => this.fetchAndStoreBrands() },
          { name: 'promotions', fn: () => this.fetchAndStorePromotions() }
        ];

        for (const op of operations) {
          try {
            const success = await op.fn();
            if (success) {
              result.updated.push(op.name);
              console.log(`✅ ${op.name} carregado com sucesso`);
            } else {
              result.errors.push(`Falha ao carregar ${op.name}`);
              console.warn(`⚠️ Falha ao carregar ${op.name}`);
            }
          } catch (error) {
            result.errors.push(`Erro ao carregar ${op.name}: ${error.message}`);
            console.error(`❌ Erro ao carregar ${op.name}:`, error);
          }
        }

        this.setLocalContext(remoteContext);
      } else {
        const updatesNeeded = this.getUpdatesNeeded(remoteContext, localContext);
        
        if (updatesNeeded.length === 0) {
          console.log('✨ Todos os dados estão atualizados');
          // Inclui todos os tipos para garantir hidratação do Redux a partir do localStorage
          result.updated = ['products', 'categories', 'brands', 'promotions'];
          return result;
        }

        console.log('🔄 Atualizações necessárias:', updatesNeeded);

        const updateOperations = {
          products: () => this.fetchAndStoreProducts(),
          categories: () => this.fetchAndStoreCategories(),
          brands: () => this.fetchAndStoreBrands(),
          promotions: () => this.fetchAndStorePromotions()
        };

        for (const updateType of updatesNeeded) {
          try {
            const success = await updateOperations[updateType]();
            if (success) {
              result.updated.push(updateType);
              console.log(`✅ ${updateType} atualizado com sucesso`);
            } else {
              result.errors.push(`Falha ao atualizar ${updateType}`);
              console.warn(`⚠️ Falha ao atualizar ${updateType}`);
            }
          } catch (error) {
            result.errors.push(`Erro ao atualizar ${updateType}: ${error.message}`);
            console.error(`❌ Erro ao atualizar ${updateType}:`, error);
          }
        }

        this.setLocalContext(remoteContext);
      }

      console.log('🎉 Sincronização concluída:', result);
      return result;
      
    } catch (error) {
      console.error('❌ Erro na sincronização:', error);
      return {
        success: false,
        error: error.message,
        updated: [],
        errors: [error.message]
      };
    }
  }

  /**
   * Obtém dados armazenados localmente
   * @param {string} dataType - Tipo de dados (products, categories, brands, promotions)
   * @returns {Array|null} Dados armazenados ou null
   */
  getLocalData(dataType) {
    try {
      const key = STORAGE_KEYS[dataType.toUpperCase()];
      if (!key) return null;
      
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error(`Erro ao ler ${dataType} local:`, error);
      return null;
    }
  }

  /**
   * Limpa todos os dados armazenados
   */
  clearAllData() {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    console.log('🧹 Todos os dados locais foram limpos');
  }

  /**
   * Inicia sincronização automática a cada 5 minutos
   * @returns {number} ID do intervalo para cancelamento
   */
  startAutoSync() {
    const SYNC_INTERVAL = 5 * 60 * 1000;
    
    console.log('🔄 Iniciando auto-sync a cada 5 minutos');
    
    return setInterval(async () => {
      try {
        await this.autoSync();
      } catch (error) {
        console.error('❌ Erro no auto-sync interval:', error);
      }
    }, SYNC_INTERVAL);
  }

  /**
   * Para a sincronização automática
   * @param {number} intervalId - ID do intervalo retornado por startAutoSync
   */
  stopAutoSync(intervalId) {
    if (intervalId) {
      clearInterval(intervalId);
      console.log('⏹️ Auto-sync parado');
    }
  }
}

export default new GlobalContextService();